import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Label } from './ui/label';

interface InlineDatePickerProps {
  id: string;
  label: React.ReactNode;
  value: string; // 'YYYY-MM-DD'
  onChange: (value: string) => void;
  hint?: string;
}

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(str: string): string {
  if (!str) return '';
  return parseLocalDate(str).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

const InlineDatePicker: React.FC<InlineDatePickerProps> = ({
  id,
  label,
  value,
  onChange,
  hint,
}) => {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const calendarRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Sync calendar view to selected date when opening
  useEffect(() => {
    if (open && value) {
      const d = parseLocalDate(value);
      setCurrentMonth(d.getMonth());
      setCurrentYear(d.getFullYear());
    } else if (open && !value) {
      const now = new Date();
      setCurrentMonth(now.getMonth());
      setCurrentYear(now.getFullYear());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const getDaysInMonth = (month: number, year: number) =>
    new Date(year, month + 1, 0).getDate();

  const getFirstDayOfMonth = (month: number, year: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 7 : day;
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    onChange(toDateString(date));
    setOpen(false);
  };

  const selectedDate = value ? parseLocalDate(value) : undefined;

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const daysInPrevMonth = getDaysInMonth(currentMonth - 1, currentYear);
    const days: { day: number; isCurrentMonth: boolean; isSelected: boolean }[] = [];

    for (let i = firstDay - 1; i > 0; i--) {
      days.push({ day: daysInPrevMonth - i + 1, isCurrentMonth: false, isSelected: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = !!selectedDate &&
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentMonth &&
        selectedDate.getFullYear() === currentYear;
      days.push({ day, isCurrentMonth: true, isSelected });
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({ day, isCurrentMonth: false, isSelected: false });
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  };

  const weeks = renderCalendarDays();

  // Measure calendar height for smooth animation
  const [calendarHeight, setCalendarHeight] = useState(0);
  useEffect(() => {
    if (calendarRef.current) {
      setCalendarHeight(calendarRef.current.scrollHeight);
    }
  }, [open, currentMonth, currentYear]);

  return (
    <div ref={wrapperRef} className="space-y-1.5">
      <Label htmlFor={id} className="text-xs font-medium text-foreground sm:text-sm">
        {label}
      </Label>
      <button
        id={id}
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex w-full items-center justify-between rounded-lg border bg-background px-3 py-2.5 text-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary/50 ${
          open
            ? 'border-primary ring-1 ring-primary/50'
            : 'border-input hover:bg-accent/30'
        }`}
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
          {value ? formatDisplayDate(value) : 'Seleccioná una fecha...'}
        </span>
        <CalendarDays className={`h-4 w-4 shrink-0 transition-colors ${open ? 'text-primary' : 'text-muted-foreground'}`} />
      </button>

      {/* Animated calendar container */}
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{
          maxHeight: open ? `${calendarHeight + 16}px` : '0px',
          opacity: open ? 1 : 0,
        }}
      >
        <div
          ref={calendarRef}
          className="rounded-xl border border-border bg-card shadow-lg p-4 mt-1.5"
        >
          {/* Month Navigation */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-8 border border-border w-full justify-between rounded-xl py-0.5 px-0.5 text-sm font-medium text-foreground">
              <button
                type="button"
                onClick={handlePreviousMonth}
                className="p-2 transition-all duration-300 rounded-lg text-foreground hover:bg-primary/10 hover:text-primary"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-semibold">
                {MONTHS[currentMonth]} {currentYear}
              </span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-2 transition-all duration-300 rounded-lg text-foreground hover:bg-primary/10 hover:text-primary"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <table className="w-full">
            <thead>
              <tr className="flex">
                {DAYS.map((day) => (
                  <td key={day} className="flex-1 flex items-center justify-center h-9">
                    <p className="text-xs font-medium text-muted-foreground">{day}</p>
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, weekIndex) => (
                <tr key={weekIndex} className="flex">
                  {week.map((dayObj, dayIndex) => (
                    <td key={dayIndex} className="flex-1 flex items-center justify-center h-9">
                      <button
                        type="button"
                        onClick={() => dayObj.isCurrentMonth && handleDateClick(dayObj.day)}
                        disabled={!dayObj.isCurrentMonth}
                        className={`text-sm font-medium rounded-full flex items-center justify-center w-8 h-8 transition-all duration-200 ${
                          dayObj.isSelected
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : dayObj.isCurrentMonth
                              ? 'text-foreground hover:bg-primary/10 hover:text-primary'
                              : 'text-muted-foreground/40 cursor-default'
                        }`}
                      >
                        {dayObj.day}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Today shortcut */}
          <div className="mt-2 pt-2 border-t border-border flex justify-center">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                onChange(toDateString(today));
                setOpen(false);
              }}
              className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-3 py-1 rounded-md hover:bg-primary/5"
            >
              Hoy
            </button>
          </div>
        </div>
      </div>

      {hint && !open && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

export default InlineDatePicker;
